/**
 * The player. The spine of the whole screen.
 *
 * Everything below it — the transcript, the rail, the charts — is indexed
 * against the position this card owns, so it sits at the top, stays there while
 * you scroll the transcript, and is the one thing on the page that is never
 * more than a glance away.
 *
 * The media element is real, pointed straight at the BFF's byte-range proxy,
 * and hidden when it is an `<audio>`. Its native controls are not used because
 * they cannot show the activity ribbon, which is the entire reason this player
 * is worth building; but everything native is preserved — ranged requests,
 * seeking, playback rate, media keys.
 *
 * ===========================================================================
 * WHEN THERE IS RETAINED VIDEO, THE VIDEO *IS* THIS PLAYER
 *
 * It does not sit beside the audio bar and it is not a fourth tab. Three
 * reasons, in the order that decided it:
 *
 * **One clock.** The page's premise, stated at the top of
 * `routes/session-detail.tsx`, is that there is exactly one clock and
 * everything reads from it. Two media elements are two clocks: the transcript
 * can only follow one, the ribbon can only draw one, and keeping them in step
 * means a seek listener on each writing `currentTime` into the other — which
 * drifts, fights the buffering element, and turns every word click into a race.
 * A tab does not avoid this, it just hides one of the two clocks off-screen
 * while it keeps running.
 *
 * **The composite already contains the audio.** It is an H.264/AAC MP4 of the
 * same conversation on the same timeline — verified: `ffprobe` reports a 49.000 s
 * video stream and a 48.995 s AAC stream in the reference render. Playing the
 * mixed Ogg *as well* would be the same audio twice.
 *
 * **The phone.** This is where a tab looks tempting and is worst. A 16:9 surface
 * at 375px is 211px, so the pinned spine goes from ~150px to ~300px of a ~560px
 * scrollport and the transcript drops from about twelve visible lines to five.
 * That is a real cost — so the picture COLLAPSES, with the transport, the
 * ribbon, the clock and the media element all staying exactly where they are.
 * Collapsed, the layout is byte-for-byte the audio-only one this screen already
 * shipped; expanded, you are watching. It is a view toggle, not a source
 * switch: nothing re-buffers, the position does not move, and the transcript
 * never loses its clock. Putting the video behind a tab would have made that
 * same choice permanent and unavailable.
 *
 * The honest cost of collapsing rather than switching sources is that a viewer
 * listening to a collapsed video is still streaming 720p. Switching to the
 * audio artifact instead would save that bandwidth and cost a re-buffer, a
 * re-seek and a second code path through the clock; on a screen whose whole
 * point is that the clock is single and trustworthy, that trade goes this way.
 * ===========================================================================
 */

import * as React from 'react'
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Video,
} from 'lucide-react'
import type { ArtifactRef } from '@era/shared'
import { Card } from '@/components/ui/card'
import { artifactUrl } from '@/lib/api'
import { formatBytes, formatDuration } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Timeline, type TimelineLane } from './timeline'
import { useClockEffect, type Player } from '../use-player'
import type { VideoRenderStatus } from '../video'

/** The four that are actually useful. 0.75× is a novelty; 3× is unintelligible. */
const RATES = [1, 1.25, 1.5, 2] as const

/** The next rate in the cycle, wrapping. An unknown rate restarts at 1×. */
function nextRate(current: number): number {
  const at = (RATES as readonly number[]).indexOf(current)
  return RATES[(at + 1) % RATES.length]!
}

/** What the card needs to draw a video surface. Absent for an audio-only call. */
export interface PlayerVideo {
  render: VideoRenderStatus
  /** From `render.width/height`, so the box is the right shape before load. */
  aspectRatio: number
  /** The one sentence that stops a legitimately odd video looking broken. */
  caveat: string | null
}

export function PlayerCard({
  player,
  sessionId,
  artifact,
  video,
  lanes,
  hasActivity,
}: {
  player: Player
  sessionId: string
  /**
   * The mixed-audio artifact. Null for a session that has retained video and no
   * audio playback track. Video mode always enables the audio child, but no
   * eligible microphone or a partial audio failure can still leave a valid
   * video render with silent AAC, so this cannot be required.
   */
  artifact: ArtifactRef | null
  /** Present only for a ready render. When set, THIS is the media element. */
  video: PlayerVideo | null
  lanes: TimelineLane[]
  /** False while the activity document is still being made — see the caption. */
  hasActivity: boolean
}) {
  // `videoUrl` mirrors upstream's own path shape. There is no artifact id
  // because retained video is not an artifact: it is a separate channel with a
  // separate route, and which render the route serves is upstream's decision.
  const src = video
    ? `/api/sessions/${encodeURIComponent(sessionId)}/video`
    : artifact
      ? artifactUrl(sessionId, artifact.artifact_id)
      : null
  const clockRef = React.useRef<HTMLSpanElement | null>(null)

  /*
   * Expanded by default, and NOT persisted.
   *
   * Someone who opens a session that has video came to see it, and a player
   * that opens collapsed makes the feature invisible to anyone who does not
   * notice a chevron. Remembering the choice across sessions was considered and
   * dropped: the right answer differs per recording (a screen share you read
   * versus a talking head you listen to), so a sticky preference would be wrong
   * about half the time and silently.
   */
  const [videoExpanded, setVideoExpanded] = React.useState(true)

  useClockEffect(
    player,
    (timeMs) => {
      if (clockRef.current) clockRef.current.textContent = formatDuration(timeMs)
    },
    [],
  )

  // Space toggles playback from anywhere on the page, because on a screen whose
  // job is listening it is the one control people reach for without looking.
  // Suppressed while typing, or the transcript search box would eat its own
  // spaces and start the audio.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space' || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, [contenteditable="true"], [role="slider"], button')) {
        return
      }
      event.preventDefault()
      player.toggle()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [player])

  return (
    /**
     * NOT sticky itself any more, and that is a deliberate move rather than a
     * removal — see `PlayerSpine` in `routes/session-detail.tsx`, which is now
     * the pinned element. The card could not be both sticky and measured (the
     * `Card` primitive still carries a comment about why a measuring wrapper
     * breaks stickiness), and on a phone the thing that has to be pinned is the
     * player AND the tab strip under it as one block, which is a wrapper's job.
     */
    <Card className="overflow-hidden">
      {/*
        EXACTLY ONE MEDIA ELEMENT, EVER.

        `player.attach` takes an `HTMLMediaElement`, so the same clock drives
        whichever of these is rendered and the transcript, the ribbon and the
        charts do not know the difference. Rendering both and hiding one would
        reintroduce the second clock this design exists to avoid — and a hidden
        `<video>` keeps buffering.

        `preload="metadata"` on both: it fetches the few bytes needed to learn
        the duration and stops. Preloading every session somebody opens would
        pull whole recordings out of B2 unasked, and for a 720p composite that
        is megabytes rather than kilobytes.
      */}
      {video ? (
        <div
          className={cn(
            // `bg-black` rather than the card surface. A black matte is what a
            // viewer reads as "this is the video"; a grey one reads as a broken
            // image. It is also what the black lead-in blends into, so a call
            // whose camera came on late does not look like a failed load.
            'relative mx-auto w-full overflow-hidden bg-black transition-[max-height] duration-200',
            // Collapsed is a real 0, not `display:none`: the element must stay
            // in the tree and keep playing, so a collapsed video's audio
            // continues and the clock never stops. `overflow-hidden` hides it.
            !videoExpanded && 'max-h-0',
          )}
          style={{
            // ============================================================
            // TWO CONSTRAINTS, AND BOTH WERE MEASURED IN A REAL BROWSER.
            //
            // `aspectRatio` comes from `render.width/height`, which is why
            // those are carried through the BFF at all. A `<video>` with no
            // intrinsic size is 300x150 until metadata lands; on a screen whose
            // player is PINNED, that swap moves the tab strip and the whole
            // transcript under the reader's thumb. With this the box is the
            // right shape from the first paint and nothing shifts.
            //
            // `maxWidth` is the fix for a bug this actually had. Aspect ratio
            // alone means the picture is as tall as the card is wide: measured
            // at 1102x620 on a 1280x720 desktop viewport, which is a pinned
            // player taking 86% of the screen and a transcript reduced to
            // nothing. Capping HEIGHT and letting `object-contain` letterbox
            // would have worked and would have put wide black bars either side;
            // capping WIDTH at `height x aspect` instead makes the box exactly
            // the picture — bounded height, no bars, and it centres itself with
            // `mx-auto`.
            //
            // `dvh`, not `vh`. This is a cap on one element, not the shell's
            // layout height — the shell still owns that, and there is no
            // `100vh`/`h-screen` anywhere here. `dvh` is the unit that tracks
            // iOS's URL bar collapsing, so the cap stays honest as the viewport
            // changes instead of being computed against the tallest possible
            // one. On a 375x667 phone 38dvh is 253px and the full-width picture
            // is 211px, so the cap does nothing there and the phone keeps the
            // edge-to-edge video it should have.
            // ============================================================
            aspectRatio: videoExpanded ? video.aspectRatio : undefined,
            maxWidth: `calc(38dvh * ${video.aspectRatio})`,
          }}
        >
          <video
            ref={player.attach}
            src={src ?? undefined}
            preload="metadata"
            // No `controls`: the ribbon below is the scrubber, and a native
            // control bar would be a second, differently-shaped transport for
            // the same clock. `playsInline` is not optional — without it iOS
            // Safari takes any playing video fullscreen, which would blow away
            // the transcript this player exists to sit above, and the screen
            // deliberately has no automatic fullscreen anywhere.
            playsInline
            className="absolute inset-0 size-full object-contain"
          />
        </div>
      ) : (
        <audio ref={player.attach} src={src ?? undefined} preload="metadata" className="sr-only" />
      )}

      {video ? (
        <VideoBar
          video={video}
          expanded={videoExpanded}
          onToggle={() => setVideoExpanded((open) => !open)}
        />
      ) : null}

      {/*
        THE PLAY BUTTON MOVES, THE RIBBON DOES NOT.

        On a desktop the button sits in its own column to the left of the
        ribbon. On a phone that column costs the ribbon 60 of the 311 px it
        could have — and the ribbon is the scrub bar for a recording that may be
        forty minutes long, so those 60px are the difference between one pixel
        being ten seconds and one pixel being eight. So below `sm` the button
        drops into the transport row underneath and the ribbon runs the full
        width of the card.

        Done by rendering the BUTTON twice rather than the whole card twice, and
        that is the important half. `Timeline` subscribes to the playback clock
        and writes the playhead's transform on every animation frame; a second
        copy of it would keep doing that from behind `display: none`, sixty
        times a second, for a ribbon nobody can see. A button is stateless.
      */}
      <div className="flex flex-wrap items-start gap-3 px-4 py-3 sm:flex-nowrap sm:gap-5 sm:px-6 sm:py-5">
        <PlayButton player={player} className="mt-1 hidden sm:flex" />

        <div className="w-full min-w-0 flex-1 space-y-2 sm:space-y-2.5">
          <Timeline
            player={player}
            lanes={lanes}
            durationMs={player.durationMs}
            laneHeight={lanes.length > 3 ? 9 : 12}
            // Set in CSS rather than passed as a number so it can differ by
            // viewport without a JavaScript breakpoint. A six-person call is
            // six lanes, and six 12px lanes plus their gaps is 92px — a
            // seventh of an iPhone SE spent on the ribbon alone.
            className={cn(
              lanes.length > 3 ? '[--era-lane:8px]' : '[--era-lane:10px]',
              'sm:[--era-lane:initial]',
            )}
          />

          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 sm:gap-x-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <PlayButton player={player} className="flex sm:hidden" />
              <p className="text-sm tabular text-ink">
                <span ref={clockRef}>0:00</span>
                <span className="text-ink-dim"> / {formatDuration(player.durationMs)}</span>
              </p>
              <div className="flex items-center gap-0.5">
                <NudgeButton onClick={() => player.nudge(-10_000)} label="Back 10 seconds">
                  <RotateCcw className="size-4 sm:size-3.5" />
                </NudgeButton>
                <NudgeButton onClick={() => player.nudge(10_000)} label="Forward 10 seconds">
                  <RotateCw className="size-4 sm:size-3.5" />
                </NudgeButton>
              </div>
            </div>

            {/* Four rate buttons at 34x20px are four miss targets, and on a
                390px phone the row they sit in wraps onto a second line — 24px
                of the viewport spent so that 1.25x and 1.5x can both be one tap
                away. On touch they collapse into ONE 44px control that cycles,
                which is the idiom every podcast app on the platform uses. The
                desktop row is untouched: with a cursor, four targets that need
                no state to read are strictly better than one that does. */}
            <button
              type="button"
              onClick={() => player.setRate(nextRate(player.rate))}
              aria-label={`Playback speed ${player.rate}×. Tap to change.`}
              className={cn(
                'flex h-11 min-w-11 items-center justify-center rounded-md px-2 text-sm tabular',
                'font-semibold transition-colors active:bg-surface-3 sm:hidden',
                player.rate === 1 ? 'text-ink-muted' : 'bg-accent-soft text-accent',
              )}
            >
              {player.rate}×
            </button>

            <div className="hidden items-center gap-1 sm:flex">
              <span className="eyebrow mr-1">speed</span>
              {RATES.map((rate) => (
                <button
                  key={rate}
                  type="button"
                  onClick={() => player.setRate(rate)}
                  aria-pressed={player.rate === rate}
                  className={cn(
                    'rounded px-1.5 py-0.5 text-xs tabular transition-colors',
                    player.rate === rate
                      ? 'bg-accent-soft font-semibold text-accent'
                      : 'text-ink-dim hover:text-ink',
                  )}
                >
                  {rate}×
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* The provenance strip.
          Hidden on a phone unless it is carrying an ERROR, and that asymmetry
          is the point: at 390px it wraps to three lines — ~76px, half the
          height of the player above it — to say the content type and the byte
          count of a file whose every detail is one tap away in Files. The error
          is the opposite kind of fact: it is the only thing on the screen that
          explains why nothing plays, and it must never be the thing that got
          folded away. */}
      <div
        className={cn(
          'flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-4 py-2.5',
          'text-[0.6875rem] text-ink-dim sm:px-6',
          player.error ? 'flex' : 'hidden sm:flex',
        )}
      >
        {player.error ? (
          <span className="text-critical">{player.error}</span>
        ) : (
          <>
            <span>
              {video ? (
                <>
                  selected video · {video.render.content_type} ·{' '}
                  {formatBytes(video.render.size_bytes ?? null)}
                </>
              ) : artifact ? (
                <>
                  mixed track · {artifact.content_type ?? 'audio'} ·{' '}
                  {formatBytes(artifact.size_bytes)}
                </>
              ) : (
                <>no stored media</>
              )}
            </span>
            <span aria-hidden>·</span>
            <span>streamed through the control plane&rsquo;s authenticated proxy</span>
            {/* The mixed audio does not disappear when video takes the player —
                it is still in Files, and saying so is what stops "where did my
                audio recording go" being a support question. */}
            {video && artifact ? (
              <>
                <span aria-hidden>·</span>
                <span>the audio-only mix is in Files</span>
              </>
            ) : null}
            {!hasActivity ? (
              <>
                <span aria-hidden>·</span>
                {/* Said out loud rather than left as an unexplained flat bar. */}
                <span>the per-speaker ribbon appears when the activity document is ready</span>
              </>
            ) : null}
          </>
        )}
      </div>
    </Card>
  )
}

/**
 * The strip between the picture and the transport: what this video is, and the
 * one control that changes how much of the screen it takes.
 *
 * It stays visible when the picture is collapsed — that is the whole point.
 * Collapsed, this row is the only thing telling the reader there IS a video and
 * that one tap brings it back; without it the feature vanishes and the player
 * silently becomes an audio bar that is streaming 720p for no visible reason.
 */
function VideoBar({
  video,
  expanded,
  onToggle,
}: {
  video: PlayerVideo
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="border-b border-line">
      <div className="flex items-center gap-2 px-4 py-1.5 sm:px-6">
        <Video className="size-3.5 shrink-0 text-ink-dim" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-[0.6875rem] text-ink-dim">
          retained video
          {video.render.height ? ` · ${video.render.height}p` : ''}
        </span>
        <button
          type="button"
          onClick={onToggle}
          // `aria-expanded` and a spelled-out label rather than an icon alone:
          // collapsed, this button is the ONLY route back to the picture, so a
          // screen reader has to be able to find it by name.
          aria-expanded={expanded}
          aria-label={expanded ? 'Hide the video' : 'Show the video'}
          // 44px tall on touch, and it stays inside the strip's height by
          // hanging its padding off a negative margin — the same trick the
          // dismiss button in `states.tsx` uses, for the same reason: a touch
          // target must not cost the pinned spine 28px of every viewport.
          className="tap44 -my-2 flex shrink-0 items-center gap-1 rounded-md px-2 py-2 text-[0.6875rem] font-medium text-ink-muted transition-colors hover:text-ink active:bg-surface-3"
        >
          {expanded ? 'hide' : 'show'}
          {expanded ? (
            <ChevronUp className="size-3.5" aria-hidden />
          ) : (
            <ChevronDown className="size-3.5" aria-hidden />
          )}
        </button>
      </div>

      {/* The caveat is NOT hidden with the picture. "The first 19 seconds are
          black" explains something the reader is looking at right now, and a
          collapsed video is exactly when a viewer is most likely to be
          wondering why they saw nothing. */}
      {video.caveat ? (
        <p className="px-4 pb-2 text-[0.6875rem] leading-relaxed text-warn sm:px-6">
          {video.caveat}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Rendered twice — once in the desktop column, once in the phone's transport
 * row — with exactly one of the two visible. See the comment at the card body.
 */
function PlayButton({ player, className }: { player: Player; className?: string }) {
  return (
    <button
      type="button"
      onClick={player.toggle}
      disabled={player.error !== null}
      aria-label={player.playing ? 'Pause' : 'Play'}
      className={cn(
        'size-12 shrink-0 items-center justify-center rounded-full',
        'bg-accent text-accent-fg shadow-sm transition-[filter,opacity]',
        'hover:brightness-110 active:brightness-95 disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
    >
      {player.waiting && player.playing ? (
        <Loader2 className="size-5 animate-spin" />
      ) : player.playing ? (
        <Pause className="size-5" />
      ) : (
        <Play className="ml-0.5 size-5" />
      )}
    </button>
  )
}

function NudgeButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      // 44px on a phone, the desktop's 28px above `sm`. A ±10s skip is the
      // control people hit most often while listening and the one they hit
      // without looking, so it is the last one that should be small.
      className="flex size-11 items-center justify-center rounded-md text-ink-dim transition-colors active:bg-surface-3 hover:bg-surface-2 hover:text-ink sm:size-7"
    >
      {children}
    </button>
  )
}
